import React, { createContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import zionDebug, { debugVerbose } from '@fs/zion-debug'
import { fetchTargetMboxOffers, shouldSkipAdobeTarget } from './adobeTargetService'

const debug = zionDebug('zion:adobe-target:AdobeTargetProvider:')
const trace = debugVerbose('zion:adobe-target:AdobeTargetProvider:')
export const ATContext = createContext({})

/**
 * Filters out new activity that haven't already been registered.
 */
function getUniqueTargetActivities(newTargetActivities, prevTargetActivities) {
  const newLocation = new Set(newTargetActivities.map((activity) => activity.location))
  const prevActivityLocations = new Set(prevTargetActivities.map((activity) => activity.location))
  const activityLocationsToAdd = [...newLocation].filter((location) => !prevActivityLocations.has(location))
  debug('getUniqueTargetActivities - new locations to add:', activityLocationsToAdd)
  return newTargetActivities.filter((activity) => activityLocationsToAdd.includes(activity.location))
}

/**
 * Converts mockActivities to a getOfferResponses object where each activity location has its response data
 */
function convertMockActivitiesToResponses(mockActivities) {
  if (!mockActivities) return {}

  // Create a response object with location as keys
  return Object.entries(mockActivities).reduce((responses, [key, value]) => {
    responses[key] = {
      location: key,
      experienceId: value.experienceId,
    }
    return responses
  }, {})
}

const initialState = {
  targetStatus: 'loading',
  getOfferResponses: {},
  loading: true,
  errors: {}, // Track errors by location instead of a single error
  targetActivities: [],
  useMocks: false,
}

/**
 * Extracts Processes the  results of settled promises from Adobe Target activities
 * and transforms them into a consistent format.
 *
 * @param {Array} targetPromises - Results from Promise.allSettled() for Target activities
 * @param {Object} currentResults - Current map of Target location results
 * @return {Object} Updated map of Target location results with experiences
 */
export function extractResultsFromTargetPromises(targetPromises, currentResults) {
  const updatedResults = { ...currentResults }

  targetPromises.forEach((result) => {
    if (result.status === 'fulfilled') {
      // For successful promises, extract the experience data
      const { location } = result.value
      updatedResults[location] = result.value
    } else {
      // For failed promises, extract error information with defaults
      const { location, experienceIds, status } = result.reason
      updatedResults[location] = { status, location, experienceIds }
    }
  })

  return updatedResults
}

/**
 * Initializes state with mock data if provided.
 */
function getInitialState(mockActivities) {
  if (mockActivities) {
    // mockActivities can be a map or a promise that resolves to a map
    if (!(mockActivities instanceof Promise)) {
      return {
        ...initialState,
        targetStatus: 'skipped',
        loading: false,
        getOfferResponses: convertMockActivitiesToResponses(mockActivities),
        targetActivities: Object.entries(mockActivities).map(([key, value]) => ({
          location: key,
          experienceIds: [value.experienceId],
        })),
        useMocks: true,
      }
    }
    // mockActivities is a promise, wait until it resolves
    return {
      ...initialState,
      targetStatus: 'pending',
      loading: true,
    }
  }
  return initialState
}

/**
 * Reducer to manage Adobe Target state transitions.
 */
function targetReducer(state, action) {
  debug('targetReducer received action:', action)
  trace('targetReducer previous state:', state)
  switch (action.type) {
    case 'SET_TARGET_STATUS':
      if (action.status === 'skipped') {
        return { ...state, targetStatus: action.status, loading: false, useMocks: true }
      }
      return { ...state, targetStatus: action.status }
    case 'SET_LOADING':
      return { ...state, loading: action.loading }
    case 'SET_TIMED_OUT':
      return {
        ...state,
        errors: { global: new Error('Adobe Target load timed out') },
        status: 'timedout',
        loading: false,
      }
    case 'UPDATE_MOCK_ACTIVITIES':
      return {
        ...state,
        getOfferResponses: convertMockActivitiesToResponses(action.mockActivities),
        targetActivities: Object.entries(action.mockActivities).map(([key, value]) => ({
          location: key,
          experienceIds: [value.experienceId],
        })),
        useMocks: true,
        loading: false,
        targetStatus: 'skipped',
      }
    case 'UPDATE_GET_OFFER_RESPONSES': {
      // Skip updating getOfferResponses if using mocks
      if (state.useMocks) {
        debug('Using mock experiences, mocking target offers')
        return {
          ...state,
          getOfferResponses: action.getOfferResponses,
          loading: false,
        }
      }

      debug('Raw getOfferResponses:', action.getOfferResponses)
      return {
        ...state,
        getOfferResponses: action.getOfferResponses,
        loading: false,
        // Only keep global errors and add any new location-specific errors
        errors: {
          ...(state.errors.global ? { global: state.errors.global } : {}),
          ...action.locationErrors, // This will be passed in from the promise handler
        },
      }
    }

    case 'REGISTER_ACTIVITIES': {
      if (!action.activityConfig || !action.activityConfig.location) return state
      debug('Registering activityConfig:', action.activityConfig)
      const uniqueTargetActivities = getUniqueTargetActivities([action.activityConfig], state.targetActivities)
      if (uniqueTargetActivities.length === 0) return state
      return {
        ...state,
        targetActivities: [...state.targetActivities, ...uniqueTargetActivities],
        // Set loading to true when new experiments are registered, and we're not using mocks
        loading: !state.useMocks,
      }
    }

    case 'SET_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          ...(action.location
            ? { [action.location]: new Error(action.errorMessage) }
            : { global: new Error(action.errorMessage) }),
        },
        loading: false,
      }
    default:
      debug('Unknown action type:', action.type)
      return state
  }
}

/**
 * Provider component that manages Adobe Target integration.
 */
export default function AdobeTargetProvider({ mockActivities, children }) {
  const [state, dispatch] = useReducer(targetReducer, getInitialState(mockActivities))
  const { targetStatus, getOfferResponses, loading, errors, targetActivities, useMocks } = state
  const timeoutRef = useRef(null)

  // Determine if Adobe Target should be skipped
  const isBot = typeof navigator !== 'undefined' && navigator.userAgent.includes('prerender')
  const isSafari = typeof navigator !== 'undefined' && /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  const skipAdobeTarget = shouldSkipAdobeTarget(isBot, isSafari) || useMocks

  useMemo(() => {
    debug('mockActivities changed:', mockActivities)
    if (mockActivities && Object.keys(mockActivities).length > 0) {
      dispatch({ type: 'UPDATE_MOCK_ACTIVITIES', mockActivities })
    }
  }, [mockActivities])

  /**
   * Handles Adobe Target loading lifecycle:
   * - Sets timeout fallback
   * - Listens for 'at-library-loaded' event
   * - Detects if already loaded
   */
  useEffect(() => {
    // don't load Adobe Target if we are waiting for mockActivities promise
    if (mockActivities instanceof Promise && targetStatus === 'pending') {
      return undefined
    }
    if (skipAdobeTarget) {
      debug(`Skipping Adobe Target due to: ${useMocks ? 'using mocks' : 'prerender bots or safari'}`)
      dispatch({ type: 'SET_TARGET_STATUS', status: 'skipped' })
      return undefined
    }

    const handleAtLoaded = () => {
      debug('AT: Loaded event')
      dispatch({ type: 'SET_TARGET_STATUS', status: 'loaded' })
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }

    timeoutRef.current = setTimeout(() => {
      debug('Adobe Target load timed out')
      dispatch({ type: 'SET_TIMED_OUT' })
    }, 10000)

    if (typeof window !== 'undefined' && window.adobe?.target) {
      debug('Adobe Target already loaded in window')
      handleAtLoaded()
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
      }
    }

    document.addEventListener('at-library-loaded', handleAtLoaded)
    return () => {
      document.removeEventListener('at-library-loaded', handleAtLoaded)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [mockActivities, skipAdobeTarget, targetStatus, useMocks])

  // Handle mock data updates
  // This effect might not be necessary if storybook sets new property values and trigger re-renders
  useEffect(() => {
    if (mockActivities instanceof Promise) {
      debug('Waiting for mock activies resolved promise to resolve...')
      mockActivities.then((resolvedActivities) => {
        debug('Mock activity resolved:', resolvedActivities)
        if (resolvedActivities) {
          dispatch({
            type: 'UPDATE_MOCK_ACTIVITIES',
            mockActivities: resolvedActivities,
          })
        } else {
          dispatch({ type: 'SET_TARGET_STATUS', status: 'loading' })
        }
      })
    }
  }, [mockActivities])

  /**
   * Fetches mbox offers once Adobe Target is loaded and activities are registered.
   */
  useEffect(() => {
    if (skipAdobeTarget || state.useMocks || targetStatus !== 'loaded' || targetActivities.length === 0) {
      return
    }

    // Skip fetch if there are no valid activities
    const validActivities = targetActivities.filter((activity) => typeof activity?.location === 'string')
    if (validActivities.length === 0) {
      return
    }

    debug('Fetching mbox offers:', targetActivities)

    const promises = fetchTargetMboxOffers(targetStatus, targetActivities, getOfferResponses)

    if (promises.length === 0) return

    Promise.allSettled(promises).then((results) => {
      const targetPromiseResults = extractResultsFromTargetPromises(results, getOfferResponses)

      // Track location-specific errors
      const locationErrors = {}
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const activity = targetActivities[index]
          if (activity && activity.location) {
            locationErrors[activity.location] = new Error(`Target request failed for location: ${activity.location}`)
          }
        }
      })

      dispatch({
        type: 'UPDATE_GET_OFFER_RESPONSES',
        getOfferResponses: targetPromiseResults,
        locationErrors,
      })
    })
  }, [targetStatus, targetActivities, skipAdobeTarget, getOfferResponses, state.useMocks])

  /**
   * Registers a new mbox config for Adobe Target.
   */
  const registerExperiments = useCallback((activityConfig) => {
    dispatch({ type: 'REGISTER_ACTIVITIES', activityConfig })
  }, [])

  /**
   * Context value exposed to consumers (excluding internal-only fields like getOfferResponses).
   */
  const contextValue = useMemo(
    () => ({
      getOfferResponses,
      loading,
      // Only expose global errors or errors when using mocks
      error: useMocks ? undefined : errors.global || undefined,
      errors, // Provide full error map to let consumers filter what they need
      registerExperiments,
    }),
    [getOfferResponses, loading, errors, registerExperiments, useMocks]
  )

  return <ATContext.Provider value={contextValue}>{children}</ATContext.Provider>
}
